import { useRef, useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Site } from '../../../domain/Site';

export function useSitesPage() {
  const { sites: siteRepo } = useRepositories();

  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLat, setNewLat] = useState<number | null>(null);
  const [newLng, setNewLng] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deletingSiteId, setDeletingSiteId] = useState<string | null>(null);

  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [editName, setEditName] = useState('');
  const [editLat, setEditLat] = useState<number | null>(null);
  const [editLng, setEditLng] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const load = async () => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    try {
      const { sites: list } = await siteRepo.list();
      setSites(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los campos.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    load();
  });

  const openAdd = () => {
    setNewName('');
    setNewLat(null);
    setNewLng(null);
    setFormError(null);
    setShowAdd(true);
  };

  const setNewLocation = (lat: number, lng: number) => {
    setNewLat(lat);
    setNewLng(lng);
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      setFormError('Ingresá un nombre para el campo.');
      return;
    }
    setCreating(true);
    setFormError(null);
    try {
      await siteRepo.create({ name: newName.trim(), lat: newLat, lng: newLng });
      setShowAdd(false);
      load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo crear el campo.');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (site: Site) => {
    setEditingSite(site);
    setEditName(site.name);
    setEditLat(site.lat);
    setEditLng(site.lng);
    setEditError(null);
  };

  const setEditLocation = (lat: number, lng: number) => {
    setEditLat(lat);
    setEditLng(lng);
  };

  const handleUpdate = async () => {
    if (!editingSite) return;
    if (!editName.trim()) {
      setEditError('Ingresá un nombre para el campo.');
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await siteRepo.update(editingSite.id, {
        name: editName.trim(),
        lat: editLat,
        lng: editLng,
      });
      setEditingSite(null);
      load();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'No se pudo guardar el campo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSiteId) return;
    const id = deletingSiteId;
    setDeletingSiteId(null);
    try {
      await siteRepo.delete(id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar el campo.');
    }
  };

  return {
    sites,
    loading,
    error,
    showAdd,
    setShowAdd,
    newName,
    setNewName,
    newLat,
    newLng,
    setNewLocation,
    creating,
    formError,
    openAdd,
    handleCreate,
    deletingSiteId,
    setDeletingSiteId,
    handleDelete,
    editingSite,
    setEditingSite,
    editName,
    setEditName,
    editLat,
    editLng,
    setEditLocation,
    saving,
    editError,
    openEdit,
    handleUpdate,
  };
}
